<script>
    import Picture from '$lib/components/Picture.svelte';
    import { browser } from '$app/environment';

    export let content = {};
    export let modulePosition;
    export let sectionPosition;
    export let key = sectionPosition;

    let queued; // Queue storage for video identifiers to play after videojs is loaded
    let showVideo = false; // Controls whether the video is shown

    /**
     * Initializes video players upon loading video.js. Configures players for both desktop
     * and mobile versions if applicable, and processes any queued videos.
     */
    const onVideoJSLoad = () => {
        // Configure video players
        window.videojs(`desktop${key}`);
        if (content?.mobileVersion) window.videojs(`mobile${key}`);
        // Play queued video if any
        if (queued) {
            window.videojs(queued).play();
            showVideo = true;
            queued = null; // Clear the queue after playing
        }
    };

    /**
     * Attempts to play a video on the specified device. If videojs is not loaded, it queues the video.
     * Logs an error to the console if the specified device is invalid.
     *
     * @param {'desktop' | 'mobile'} device - The target device type ('desktop' or 'mobile').
     */
    const playVideo = (device) => {
        if (device !== 'desktop' && device !== 'mobile') {
            console.error('Invalid device specified. Device should be "desktop" or "mobile".');
            return;
        }
        if (!window?.videojs) {
            queued = `${device}${key}`;
            return;
        }

        window.videojs(`${device}${key}`).play();
        showVideo = true;
    };
</script>

<svelte:head>
    <link href="https://vjs.zencdn.net/8.3.0/video-js.css" rel="stylesheet" />
    <style>
        .vjs-big-play-button {
            display: none !important;
        }
        .video-js .vjs-control-bar {
            background-color: rgba(0, 0, 0, 0.6);
        }
    </style>
</svelte:head>

<div data-moduleposition={modulePosition}>
    <div class="hidden overflow-hidden rounded-2xl shadow-lg lg:block">
        <div class={`${showVideo ? 'block' : 'hidden'}`}>
            <video id={`desktop${key}`} class="video-js vjs-fluid" controls preload="auto" poster={content?.thumbUrl}>
                <track kind="captions" />
                <source src={content?.videoSrc} type="video/mp4" />
            </video>
        </div>
        <button class="block w-full" on:click={() => playVideo('desktop')} class:hidden={showVideo}>
            <Picture
                src={content?.thumbUrl}
                alt="Large Video Play Thumbnail"
                width={content?.dimensions?.width}
                height={content?.dimensions?.height}
                imageSizes={[640, 1024, 1300]}
                classes="w-full"
                lazy={sectionPosition > 3}
                lcp={sectionPosition <= 3}
                lqip={content?.lqip}
            />
        </button>
    </div>

    <!---->
    {#if content?.mobileVersion}
        <div class="block overflow-hidden rounded-2xl shadow-lg lg:hidden">
            <div class={`${showVideo ? 'block' : 'hidden'}`}>
                <video id={`mobile${key}`} class="video-js vjs-fluid" controls preload="auto" poster={content?.mobilethumbUrl}>
                    <track kind="captions" />
                    <source src={content?.mobileVideoSrc} type="video/mp4" />
                </video>
            </div>
            <button class="block w-full" on:click={() => playVideo('mobile')} class:hidden={showVideo}>
                <Picture
                    src={content?.mobilethumbUrl}
                    alt="Small Video Play Thumbnail"
                    width={content?.mobiledimensions?.width}
                    height={content?.mobiledimensions?.height}
                    imageSizes={[1024, 1024, 1300]}
                    classes="w-full"
                    lazy={sectionPosition > 3}
                    lcp={sectionPosition <= 3}
                    lqip={content?.mobilelqip}
                />
            </button>
        </div>
    {/if}
</div>

{#if browser}
    <script src="https://vjs.zencdn.net/8.3.0/video.min.js" on:load={onVideoJSLoad}></script>
{/if}
